/**
 * Relay Simulator API Client — Landlord Portal (Dev Only)
 *
 * All dev-only relay simulator endpoints under /dev/api/relay/*
 * Uses credentials: 'include' and throws on non-ok responses.
 *
 * Production hiding: fetchSimulatorEnabled() returns false when the
 * server responds with 404, signaling that the dev simulator is not
 * available. Pages should redirect to /settings/agent-email in that case.
 */

import { z } from 'zod';
import {
  MailboxSummarySchema,
  SimulatedCaseSummarySchema,
  SimulatorMessageRowSchema,
  MessageDetailSchema,
  SimulateInboundBodySchema,
} from '@resida/api-types/relay';

// ---------------------------------------------------------------------------
// Types (derived from @resida/api-types/relay Zod schemas)
// ---------------------------------------------------------------------------

// Derived from MailboxSummarySchema in @resida/api-types/relay
export type MailboxSummary = z.infer<typeof MailboxSummarySchema>;

// Derived from SimulatedCaseSummarySchema in @resida/api-types/relay
export type SimulatedCaseSummary = z.infer<typeof SimulatedCaseSummarySchema>;

// Derived from SimulatorMessageRowSchema in @resida/api-types/relay
export type SimulatorMessageRow = z.infer<typeof SimulatorMessageRowSchema>;

// Derived from MessageDetailSchema in @resida/api-types/relay
export type MessageDetail = z.infer<typeof MessageDetailSchema>;

export interface PaginatedMessages {
  messages: SimulatorMessageRow[];
  total: number;
  nextCursor: string | null;
}

export type RoutingResult =
  | { routed: true; caseId: string; messageId: string }
  | { routed: false; dropped: boolean; dropReason?: string };

// Derived from SimulateInboundBodySchema in @resida/api-types/relay
export type SimulateInboundBody = z.infer<typeof SimulateInboundBodySchema>;

export interface SimulateStatusBody {
  status: 'delivered' | 'bounced' | 'failed' | 'read';
  nativeDetail?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Base fetch helper (mirrors relayFetch pattern in relay.api.ts)
// ---------------------------------------------------------------------------

async function simulatorFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`/landlord/api/dev/relay${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  });

  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const err = await response.json();
      if (err.message) message = err.message;
      if (err.code) message = err.code;
    } catch {
      // ignore parse error
    }
    throw new Error(message);
  }

  return response.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Enabled check
// ---------------------------------------------------------------------------

/**
 * Returns true when the dev simulator is available (server returns 200 with
 * { enabled: true }). Returns false on 404 or any error — the caller should
 * redirect to /settings/agent-email.
 */
export async function fetchSimulatorEnabled(): Promise<boolean> {
  try {
    const data = await simulatorFetch<{ enabled: true }>('/enabled');
    return data.enabled === true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Cases
// ---------------------------------------------------------------------------

export async function fetchSimulatedCases(filter?: string): Promise<SimulatedCaseSummary[]> {
  const qs = filter ? `?filter=${encodeURIComponent(filter)}` : '';
  const data = await simulatorFetch<{ cases: SimulatedCaseSummary[] }>(`/cases${qs}`);
  return data.cases;
}

// ---------------------------------------------------------------------------
// Mailboxes
// ---------------------------------------------------------------------------

export async function fetchCaseMailboxes(caseId: string): Promise<MailboxSummary[]> {
  const data = await simulatorFetch<{ mailboxes: MailboxSummary[] }>(
    `/cases/${encodeURIComponent(caseId)}/mailboxes`
  );
  return data.mailboxes;
}

export async function fetchMailboxMessages(
  address: string,
  cursor?: string,
  limit?: number
): Promise<PaginatedMessages> {
  const qs = new URLSearchParams();
  if (cursor) qs.set('cursor', cursor);
  if (limit != null) qs.set('limit', String(limit));
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return simulatorFetch<PaginatedMessages>(`/mailboxes/${encodeURIComponent(address)}/messages${query}`);
}

export async function deleteMailboxSink(address: string): Promise<void> {
  await simulatorFetch<{ cleared: true }>(`/mailboxes/${encodeURIComponent(address)}`, {
    method: 'DELETE',
  });
}

export async function postSimulatedStatus(
  messageId: string,
  body: SimulateStatusBody
): Promise<void> {
  await simulatorFetch<{ ok: true }>(`/messages/${encodeURIComponent(messageId)}/status`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Inbound simulation
// ---------------------------------------------------------------------------

export async function postSimulatedInbound(body: SimulateInboundBody): Promise<RoutingResult> {
  const data = await simulatorFetch<{ result: RoutingResult }>('/inbound', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return data.result;
}

/**
 * Convenience wrapper: cold-compose inbound WITHOUT a caseId.
 * Per spec (It2-U): cold-compose omits caseId entirely.
 */
export async function postSimulatedColdInbound(
  body: Omit<SimulateInboundBody, 'caseId'>
): Promise<RoutingResult> {
  return postSimulatedInbound(body);
}
